'use client';
/**
 * Offline Indicator Component
 * Shows connectivity status + pending writes count
 */


import { useEffect, useState } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { WifiOff, RefreshCw, Upload } from 'lucide-react';

export function OfflineIndicator() {
  const { status } = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);

  // Poll pending mutation count when offline
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    async function checkPending() {
      try {
        const { getPendingMutationCount } = await import('@/lib/offline/db');
        const count = await getPendingMutationCount();
        setPendingCount(count);
      } catch {
        // DB not ready yet
      }
    }

    // Check immediately and then every 5 seconds
    checkPending();
    interval = setInterval(checkPending, 5000);

    return () => clearInterval(interval);
  }, [status]);

  // Don't show anything when online and no pending writes
  if (status === 'online' && pendingCount === 0) return null;

  // Show syncing badge when coming back online with pending writes
  if (status === 'online' && pendingCount > 0) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 animate-in slide-in-from-bottom-2">
        <Upload className="w-4 h-4 animate-bounce" />
        <span>Đang đồng bộ {pendingCount} thay đổi...</span>
      </div>
    );
  }

  return (
    <div
      className={`
        fixed bottom-4 right-4 z-50 flex items-center gap-2 
        px-3 py-2 rounded-full shadow-lg text-sm font-medium
        transition-all duration-300 animate-in slide-in-from-bottom-2
        ${status === 'offline' 
          ? 'bg-amber-100 text-amber-800 border border-amber-200' 
          : 'bg-blue-100 text-blue-800 border border-blue-200'
        }
      `}
    >
      {status === 'offline' ? (
        <>
          <WifiOff className="w-4 h-4" />
          <span>
            Offline — Dữ liệu từ bộ nhớ cục bộ
            {pendingCount > 0 && (
              <span className="ml-1 bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {pendingCount} chờ đồng bộ
              </span>
            )}
          </span>
        </>
      ) : (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Đang đồng bộ...</span>
        </>
      )}
    </div>
  );
}
